// npm
import React from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'

// react
import {MaterialText} from 'components/presentation/material-text'

// redux (selectors)
import {getExamData, getSettingsData} from 'redux/reducers/selectors'
import {getFormRunGuid} from 'redux/reducers/exam/selectors'
import {getClientSettingsData} from 'redux/reducers/settings/selectors'
import {getHighlightExamText} from 'redux/reducers/settings/client/selectors'

// highlighting
import {loadHighlights, addHighlight, removeHighlight} from './highlight-storage'
import {getSelectionOffsets, applyHighlights} from './highlight-utils'


// a scope for text that isn't owned by one question (e.g. a scenario, which comes from whichever
// ancestor section defines it and is shown on every question beneath it) - derived from the text
// itself, so every place showing the same scenario shares the same highlights
const scopeForText = (prefix, text) => {
	let hash = 5381;
	for (let i = 0; i < text.length; i++) {
		hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
	}
	return `${prefix}${(hash >>> 0).toString(36)}`;
};


// HighlightableText (not connected to store)
// --------------------------------------------------------------------------

// renders exam text through the normal MaterialText, and - when the org has HighlightExamText
// on - lets the candidate select text to highlight it, and click a highlight to remove it.
// Highlights are kept per form run + `scope` in localStorage (see highlight-storage.js).
//
// The highlight <mark>s are painted straight onto the rendered DOM, so the content is keyed on
// its scope + text: React never patches text nodes in place that the marks have split up, it
// always renders the text afresh and the marks get repainted on top.
//
// Originally by Mark Burton (HD 9205, text 5 cs:3263) for question text only - ported and
// generalised so the Scenario pane can use it too.
class HighlightableText extends React.Component {
	constructor(props) {
		super(props);
		this.containerRef = React.createRef();
		this.handleSelectionEnd = this.handleSelectionEnd.bind(this);
		this.handleClickHighlight = this.handleClickHighlight.bind(this);
	}

	componentDidMount() {
		this.paintHighlights();
	}

	componentDidUpdate(prevProps) {
		const {scope, text, enabled} = this.props;
		if (prevProps.scope !== scope || prevProps.text !== text || prevProps.enabled !== enabled) {
			this.paintHighlights();
		}
	}

	paintHighlights() {
		const container = this.containerRef.current;
		if (!container) {
			return;
		}
		const {formRunGuid, scope} = this.props;
		applyHighlights(container, loadHighlights(formRunGuid, scope), this.handleClickHighlight);
	}

	handleSelectionEnd() {
		const container = this.containerRef.current;
		if (!container) {
			return;
		}

		const offsets = getSelectionOffsets(container);
		if (!offsets) {
			return;
		}

		const {formRunGuid, scope} = this.props;
		addHighlight(formRunGuid, scope, offsets.start, offsets.end);
		window.getSelection().removeAllRanges();
		this.paintHighlights();
	}

	handleClickHighlight(start, end) {
		const {formRunGuid, scope} = this.props;
		removeHighlight(formRunGuid, scope, start, end);
		this.paintHighlights();
	}

	render() {
		const {text, className, enabled, scope} = this.props;

		if (!enabled) {
			return <MaterialText className={className}>{text}</MaterialText>;
		}

		return (
			<div
				key={`${scope}:${text}`}
				ref={this.containerRef}
				onMouseUp={this.handleSelectionEnd}
				onTouchEnd={this.handleSelectionEnd}
			>
				<MaterialText className={className} forceTextSelection>{text}</MaterialText>
			</div>
		);
	}
}

HighlightableText.propTypes = {
	text: PropTypes.string.isRequired,
	scope: PropTypes.string.isRequired,
	className: PropTypes.string,
	enabled: PropTypes.bool.isRequired,
	formRunGuid: PropTypes.string
};


// HighlightableText (connected to store)
// --------------------------------------------------------------------------

const mapStoreToProps = (store) => {
	const examData = getExamData(store);
	const clientSettingsData = getClientSettingsData(getSettingsData(store));

	return {
		enabled: getHighlightExamText(clientSettingsData),
		formRunGuid: getFormRunGuid(examData)
	};
};

HighlightableText = connect(mapStoreToProps)(HighlightableText);


// Export
// --------------------------------------------------------------------------
export {HighlightableText, scopeForText}
